import { Check, X, Clock, MinusCircle, AlertCircle, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

export interface ChecklistRouteStage {
  stage:
    | "finance_review"
    | "hr_review"
    | "sport_admin_review"
    | "ad_review"
    | "compliance_review"
    | "legal_review"
    | "signature";
  approvers: string[];
  quorum: "all" | "any";
  skipped?: boolean;
  skipReason?: string;
  /** Task #380: persona this stage was auto-routed to. */
  routedPersona?: "Finance" | "HR" | "Legal" | "SA";
  /** Task #380: human-readable routing decision (or skip reason). */
  routingReason?: string;
}

export interface ChecklistStep {
  id: number;
  stage: string;
  approverEmail: string;
  decision: string | null;
  comment: string | null;
  decidedAt: string | null;
  createdAt: string;
}

interface ApprovalChecklistOverviewProps {
  route: ChecklistRouteStage[];
  steps: ChecklistStep[];
  /** The currently active stage from the approval record. */
  currentStage: string | null;
  /** Approval status: in_progress | executed | returned | rejected. */
  approvalStatus: string | null;
  /** Email of the signed-in user — used to surface the "you're a reviewer" callout. */
  currentUserEmail?: string | null;
}

const STAGE_TITLE: Record<string, string> = {
  finance_review: "Finance Review",
  hr_review: "HR Review",
  sport_admin_review: "Sport Admin Review",
  legal_review: "Legal Review",
  compliance_review: "Compliance Review",
  ad_review: "Athletic Director Review",
  signature: "Signature",
};

const STAGE_DESCRIPTION: Record<string, string> = {
  finance_review: "Finance verifies the budget, salary and total compensation terms.",
  hr_review: "HR confirms benefits, classification and policy compliance.",
  sport_admin_review: "The sport administrator for this sport signs off on the coach and contract.",
  legal_review: "Legal counsel reviews clauses, risk and enforceability.",
  compliance_review: "Compliance verifies the contract meets policy and regulatory requirements.",
  ad_review: "Athletic Director provides final sign-off on the deal.",
  signature: "Required signers complete signature on the locked contract.",
};

/**
 * High-level checklist surfaced at the top of the Approvals tab. One row per
 * required stage so reviewers can scan the entire pipeline at a glance:
 * who is required, what they decided, when, and any comment they left.
 *
 * The detail panel below this component remains the workhorse for actions
 * (approving, returning, signing). This component is purely informational
 * but it's the single most useful "where are we?" surface on the page.
 */
export function ApprovalChecklistOverview({
  route,
  steps,
  currentStage,
  approvalStatus,
  currentUserEmail,
}: ApprovalChecklistOverviewProps) {
  if (route.length === 0) return null;

  const lowerEmail = currentUserEmail?.toLowerCase() ?? "";
  const youAreAReviewer = lowerEmail
    ? route.some((r) => r.approvers.some((a) => a.toLowerCase() === lowerEmail))
    : false;

  return (
    <div className="space-y-3" data-testid="approval-checklist-overview">
      {youAreAReviewer && (
        <div
          className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
          data-testid="approval-checklist-you-are-reviewer"
        >
          <User className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
          <div>
            <span className="font-semibold">You're listed as a reviewer on this contract.</span>{" "}
            Scroll down to the stage card below to record your decision.
          </div>
        </div>
      )}

      <ol className="rounded-lg border bg-card divide-y overflow-hidden">
        {route.map((stage, idx) => {
          const isCurrent =
            !!currentStage &&
            stage.stage === currentStage &&
            (approvalStatus === "in_progress" || approvalStatus === "returned");
          const stageStepsAll = steps.filter((s) => s.stage === stage.stage);
          // Decisions are 1-per-(approval, approver, stage) and update in
          // place, but if there's noise we want the latest per approver.
          const stageStepsByApprover = new Map<string, ChecklistStep>();
          for (const s of stageStepsAll) {
            const key = s.approverEmail.toLowerCase();
            const existing = stageStepsByApprover.get(key);
            if (!existing || new Date(s.createdAt) > new Date(existing.createdAt)) {
              stageStepsByApprover.set(key, s);
            }
          }

          let stageState: "pending" | "active" | "passed" | "failed" | "skipped" = "pending";
          if (stage.skipped) stageState = "skipped";
          else if (isCurrent) stageState = "active";
          else {
            const decisions = Array.from(stageStepsByApprover.values()).map((s) => s.decision);
            if (decisions.some((d) => d === "rejected" || d === "returned")) stageState = "failed";
            else if (decisions.length > 0 && decisions.every((d) => d === "approved")) {
              stageState = "passed";
            }
          }

          return (
            <li
              key={stage.stage}
              className={cn(
                "p-3 sm:p-4 grid grid-cols-[auto_1fr] gap-3",
                isCurrent && "bg-amber-50/40",
              )}
              data-testid={`approval-checklist-row-${stage.stage}`}
            >
              <StageIcon state={stageState} index={idx} />
              <div className="space-y-2 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-sm sm:text-base">
                    {STAGE_TITLE[stage.stage] ?? stage.stage}
                  </h4>
                  <StageStatusBadge state={stageState} />
                  <Badge variant="outline" className="text-[10.5px] uppercase tracking-wide">
                    {stage.quorum === "all" ? "All required" : "Any 1 of"}
                  </Badge>
                  {stage.routedPersona && (
                    <Badge
                      variant="secondary"
                      className="text-[10.5px]"
                      data-testid={`approval-checklist-routed-persona-${stage.stage}`}
                    >
                      Routed to {stage.routedPersona}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {STAGE_DESCRIPTION[stage.stage] ?? ""}
                </p>
                {stage.routingReason && !stage.skipped && (
                  <p className="text-[11px] text-muted-foreground italic">{stage.routingReason}</p>
                )}
                {stage.skipped && stage.skipReason && (
                  <p className="text-xs italic text-muted-foreground">Skipped: {stage.skipReason}</p>
                )}
                {!stage.skipped && (
                  <ul className="space-y-1.5">
                    {stage.approvers.map((approver) => {
                      const step = stageStepsByApprover.get(approver.toLowerCase());
                      const isYou = lowerEmail && approver.toLowerCase() === lowerEmail;
                      return (
                        <li
                          key={approver}
                          className="flex items-start gap-2 text-xs sm:text-sm"
                          data-testid={`approval-checklist-approver-${stage.stage}-${approver}`}
                        >
                          <DecisionDot decision={step?.decision ?? null} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium truncate">{approver}</span>
                              {isYou && (
                                <Badge variant="secondary" className="text-[10px] py-0 px-1.5">you</Badge>
                              )}
                              {step?.decision && (
                                <span
                                  className={cn(
                                    "text-[11px] uppercase tracking-wide font-semibold",
                                    step.decision === "approved" && "text-green-700",
                                    step.decision === "rejected" && "text-red-700",
                                    step.decision === "returned" && "text-amber-700",
                                  )}
                                >
                                  {step.decision}
                                </span>
                              )}
                              {step?.decidedAt && (
                                <span className="text-[11px] text-muted-foreground">
                                  · {safeFormat(step.decidedAt)}
                                </span>
                              )}
                            </div>
                            {step?.comment && (
                              <p
                                className="mt-0.5 rounded border bg-muted/40 px-2 py-1 text-[12px] text-foreground/80 whitespace-pre-wrap break-words"
                                data-testid={`approval-checklist-comment-${stage.stage}-${approver}`}
                              >
                                “{step.comment}”
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StageIcon({ state, index }: { state: "pending" | "active" | "passed" | "failed" | "skipped"; index: number }) {
  if (state === "passed") {
    return (
      <div className="h-7 w-7 rounded-full bg-green-600 text-white flex items-center justify-center shrink-0">
        <Check className="h-4 w-4" />
      </div>
    );
  }
  if (state === "failed") {
    return (
      <div className="h-7 w-7 rounded-full bg-red-600 text-white flex items-center justify-center shrink-0">
        <X className="h-4 w-4" />
      </div>
    );
  }
  if (state === "active") {
    return (
      <div className="h-7 w-7 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 animate-pulse">
        <Clock className="h-4 w-4" />
      </div>
    );
  }
  if (state === "skipped") {
    return (
      <div className="h-7 w-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
        <MinusCircle className="h-4 w-4" />
      </div>
    );
  }
  return (
    <div className="h-7 w-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-semibold shrink-0">
      {index + 1}
    </div>
  );
}

function StageStatusBadge({ state }: { state: "pending" | "active" | "passed" | "failed" | "skipped" }) {
  const map: Record<string, { label: string; cls: string }> = {
    passed:  { label: "Approved",   cls: "bg-green-100 text-green-800 border-green-200" },
    failed:  { label: "Action needed", cls: "bg-red-100 text-red-800 border-red-200" },
    active:  { label: "In progress", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    skipped: { label: "Skipped",    cls: "bg-muted text-muted-foreground border-border" },
    pending: { label: "Pending",    cls: "bg-slate-100 text-slate-700 border-slate-200" },
  };
  const m = map[state];
  return (
    <Badge variant="outline" className={cn("text-[10.5px] font-medium", m.cls)}>
      {m.label}
    </Badge>
  );
}

function DecisionDot({ decision }: { decision: string | null }) {
  if (decision === "approved") return <Check className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />;
  if (decision === "rejected") return <X className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />;
  if (decision === "returned") return <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />;
  return <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />;
}

function safeFormat(s: string): string {
  try { return format(new Date(s), "MMM d, h:mm a"); } catch { return s; }
}
